import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { LanguageService } from './language.service';

@Injectable({
  providedIn: 'root'
})
export class UiDomTranslationService implements OnDestroy {
  private readonly textSourceMap = new WeakMap<Text, string>();
  private readonly attributeSourceMap = new WeakMap<Element, Map<string, string>>();
  private readonly skippedTagNames = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA']);
  private readonly translatedAttributes = ['placeholder', 'title', 'aria-label'];
  private readonly subscriptions = new Subscription();
  private observer: MutationObserver | null = null;
  private translationQueued = false;
  private started = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private ngZone: NgZone,
    private languageService: LanguageService,
  ) {
    this.subscriptions.add(
      this.languageService.activeLanguageObservable.subscribe(() => this.queueTranslationPass())
    );

    this.ngZone.runOutsideAngular(() => {
      if (this.document.readyState === 'loading') {
        this.document.addEventListener('DOMContentLoaded', () => this.start(), { once: true });
        return;
      }

      this.start();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.observer?.disconnect();
  }

  private start() {
    if (this.started || !this.document?.body) {
      return;
    }

    this.started = true;
    this.observer = new MutationObserver(() => this.queueTranslationPass());
    this.observer.observe(this.document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: this.translatedAttributes,
    });

    this.queueTranslationPass();
  }

  private queueTranslationPass() {
    if (!this.started || this.translationQueued || !this.document?.body) {
      return;
    }

    this.translationQueued = true;
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.translationQueued = false;
        this.translateDocument();
      }, 0);
    });
  }

  private translateDocument() {
    const root = this.document.body;
    if (!root) {
      return;
    }

    const walker = this.document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let currentNode = walker.nextNode();
    while (currentNode) {
      this.translateTextNode(currentNode as Text);
      currentNode = walker.nextNode();
    }

    const selector = this.translatedAttributes.map(attribute => `[${attribute}]`).join(',');
    root.querySelectorAll(selector).forEach(element => this.translateElementAttributes(element));
  }

  private translateTextNode(node: Text) {
    const parentElement = node.parentElement;
    if (!parentElement || this.skippedTagNames.has(parentElement.tagName) || this.isWithinSkippedSubtree(parentElement)) {
      return;
    }

    const currentText = node.textContent || '';
    if (!currentText.trim()) {
      return;
    }

    const sourceText = this.resolveTextSource(node, currentText);
    const translatedText = this.translatePreservingWhitespace(sourceText);
    if (translatedText !== currentText) {
      node.textContent = translatedText;
    }
  }

  private translatePreservingWhitespace(value: string) {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return value;
    }

    const leadingWhitespace = value.match(/^\s*/)?.[0] || '';
    const trailingWhitespace = value.match(/\s*$/)?.[0] || '';
    const translatedCore = this.languageService.translateUiLiteral(trimmedValue);

    return `${leadingWhitespace}${translatedCore}${trailingWhitespace}`;
  }

  private resolveTextSource(node: Text, currentText: string): string {
    const existingSource = this.textSourceMap.get(node);
    if (!existingSource) {
      this.textSourceMap.set(node, currentText);
      return currentText;
    }

    if (currentText !== existingSource && !this.languageService.isUiLiteralTranslationOf(existingSource, currentText)) {
      this.textSourceMap.set(node, currentText);
      return currentText;
    }

    return existingSource;
  }

  private translateElementAttributes(element: Element) {
    if (this.isWithinSkippedSubtree(element)) {
      return;
    }

    for (const attributeName of this.translatedAttributes) {
      const currentValue = element.getAttribute(attributeName);
      if (!currentValue || !currentValue.trim()) {
        continue;
      }

      const sourceValue = this.resolveAttributeSource(element, attributeName, currentValue);
      const translatedValue = this.languageService.translateUiLiteral(sourceValue);
      if (translatedValue !== currentValue) {
        element.setAttribute(attributeName, translatedValue);
      }
    }
  }

  private resolveAttributeSource(element: Element, attributeName: string, currentValue: string): string {
    const existingMap = this.attributeSourceMap.get(element) || new Map<string, string>();
    const existingSource = existingMap.get(attributeName);
    if (!existingSource) {
      existingMap.set(attributeName, currentValue);
      this.attributeSourceMap.set(element, existingMap);
      return currentValue;
    }

    if (currentValue !== existingSource && !this.languageService.isUiLiteralTranslationOf(existingSource, currentValue)) {
      existingMap.set(attributeName, currentValue);
      this.attributeSourceMap.set(element, existingMap);
      return currentValue;
    }

    return existingSource;
  }

  private isWithinSkippedSubtree(element: Element | null) {
    return !!element?.closest('[data-i18n-skip]');
  }
}
